// NoticeDetailPage — /notice/:id
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../../shared/api/client";
import { Pill } from "../../shared/components/Pill";
import { useAuth } from "../../shared/stores/auth";
import { toast } from "../../shared/stores/toasts";
import { LinkifiedText } from "../../shared/linkify";

// ── Types ─────────────────────────────────────────────────────────────────────

type Cat = { id: number; code: string; label: string; color: string; parent_id: number | null; min_rank: number };

type Attachment = { id: number; notice_id: number; filename: string; content_type: string | null; size: number; created_at: string };

type Notice = {
  id: number; category: string; title: string; body: string;
  author_id: number; author_name: string | null;
  is_pinned: boolean; view_count: number;
  created_at: string; updated_at: string;
  attachments: Attachment[];
};

type Comment = {
  id: number; notice_id: number; author_id: number;
  author_name: string | null; body: string; created_at: string;
};

// ── Permissions ───────────────────────────────────────────────────────────────

const WRITE_ROLES = new Set(["SUPER_ADMIN","HR_ADMIN","C_LEVEL","MANAGER","TEAM_LEAD","DIVISION_HEAD","CEO"]);
const ADMIN_ROLES = new Set(["SUPER_ADMIN","HR_ADMIN"]);

// ── Helpers ───────────────────────────────────────────────────────────────────

// Pill 컴포넌트의 Kind("ok"|"warn"|"danger"|"info"|"muted"|"violet")로 매핑 —
// 이전 값(success/warning/default)은 t-pill CSS에 없어 뱃지가 무색으로 렌더됐음.
const PILL_MAP: Record<string,"info"|"danger"|"warn"|"ok"|"muted"> = {
  info:"info", danger:"danger", warning:"warn", success:"ok", default:"muted",
};
const HEX_MAP: Record<string,string> = {
  info:"#3b82f6", danger:"#ef4444", warning:"#f59e0b", success:"#22c55e", default:"#94a3b8",
};
const AVATAR_COLORS = ["#6366f1","#8b5cf6","#ec4899","#f43f5e","#f97316","#eab308","#22c55e","#14b8a6","#3b82f6","#06b6d4"];

function pillKind(c: string) { return PILL_MAP[c] ?? "info"; }
function hexColor(c: string) { return HEX_MAP[c] ?? HEX_MAP.info; }
function avatarBg(id: number) { return AVATAR_COLORS[id % AVATAR_COLORS.length]; }
function initials(name: string | null, id: number) {
  if (!name) return String(id).slice(-2);
  const p = name.trim().split(/\s+/);
  return p.length >= 2 ? p[0][0] + p[1][0] : name.slice(0, 2);
}
function fmtBytes(n: number) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
const fileIcon = (ct: string | null, name: string) => {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if ((ct ?? "").startsWith("image/")) return "🖼️";
  if (["pdf"].includes(ext)) return "📕";
  if (["xls", "xlsx", "csv"].includes(ext)) return "📊";
  if (["doc", "docx", "hwp"].includes(ext)) return "📄";
  if (["zip", "rar", "7z"].includes(ext)) return "🗜️";
  if (["mp4", "mov", "avi", "mkv"].includes(ext)) return "🎬";
  return "📎";
};

// ── Page ─────────────────────────────────────────────────────────────────────

export default function NoticeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const roles    = useAuth(s => s.manifest?.roles ?? s.user?.roles ?? []);
  const canWrite = roles.some(r => WRITE_ROLES.has(r));
  const isAdmin  = roles.some(r => ADMIN_ROLES.has(r));
  const currentUser = useAuth(s => s.user);

  const [notice,   setNotice]   = useState<Notice | null>(null);
  const [cats,     setCats]     = useState<Cat[]>([]);
  const [comments, setComments] = useState<Comment[] | null>(null);
  const [newCmt,   setNewCmt]   = useState("");
  const [posting,  setPosting]  = useState(false);
  const [loading,  setLoading]  = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    api.get<Notice>(`/notice/${id}`)
      .then(r => {
        setNotice(r.data);
        // Load category info
        api.get<Cat[]>("/notice/categories")
          .then(cr => setCats(cr.data))
          .catch(() => {});
        // Load comments
        return api.get<Comment[]>(`/notice/${id}/comments`);
      })
      .then(r => setComments(r.data))
      .catch(() => navigate("/notice", { replace: true }))
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const cat     = notice ? (cats.find(c => c.code === notice.category) ?? null) : null;
  const topCat  = cat ? (cat.parent_id ? cats.find(c => c.id === cat.parent_id) ?? null : cat) : null;
  const subTabs = topCat ? cats.filter(c => c.parent_id === topCat.id) : [];
  const tabItems = topCat ? [{ code: topCat.code, label: "전체" }, ...subTabs.map(s => ({ code: s.code, label: s.label }))] : [];

  const canEdit = canWrite && notice ? (notice.author_id === currentUser?.id || isAdmin) : false;
  const canDel  = notice ? (notice.author_id === currentUser?.id || isAdmin) : false;
  const accent  = hexColor(cat?.color ?? "info");

  const downloadAtt = async (att: Attachment) => {
    try {
      const r = await api.get(`/notice/attachments/${att.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data as Blob);
      const a = document.createElement("a");
      a.href = url; a.download = att.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error("다운로드 실패"); }
  };

  const handleDelete = async () => {
    if (!notice || !window.confirm("이 공지를 삭제하시겠습니까?")) return;
    try {
      await api.delete(`/notice/${notice.id}`);
      toast.success("공지가 삭제되었습니다");
      navigate("/notice");
    } catch (e: any) {
      toast.error("삭제 실패", e?.response?.data?.detail ?? "");
    }
  };

  const postComment = async () => {
    if (!newCmt.trim() || !notice) return;
    setPosting(true);
    try {
      await api.post(`/notice/${notice.id}/comments`, { body: newCmt.trim() });
      setNewCmt("");
      const r = await api.get<Comment[]>(`/notice/${notice.id}/comments`);
      setComments(r.data);
    } catch { toast.error("댓글 등록 실패"); }
    finally { setPosting(false); }
  };

  const delComment = async (cid: number) => {
    if (!window.confirm("댓글을 삭제하시겠습니까?")) return;
    try {
      await api.delete(`/notice/comments/${cid}`);
      setComments(prev => prev?.filter(c => c.id !== cid) ?? null);
    } catch { toast.error("댓글 삭제 실패"); }
  };

  if (loading) {
    return (
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:200, color:"var(--text-3)", fontSize:14 }}>
        불러오는 중…
      </div>
    );
  }

  if (!notice) return null;

  return (
    <div style={{ maxWidth:860, margin:"0 auto", paddingBottom:48 }}>
      {/* ── Breadcrumb / Back ──────────────────────────────────────── */}
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:20 }}>
        <button
          onClick={() => navigate("/notice")}
          style={{
            display:"flex", alignItems:"center", gap:6,
            background:"none", border:"1px solid var(--border)",
            borderRadius:8, padding:"6px 14px", cursor:"pointer",
            fontSize:13, color:"var(--text-2)",
            transition:"background 0.12s",
          }}
          onMouseEnter={e => (e.currentTarget.style.background = "var(--border)")}
          onMouseLeave={e => (e.currentTarget.style.background = "none")}
        >
          ← 목록으로
        </button>
        <span style={{ fontSize:12, color:"var(--text-3)" }}>공지사항</span>
        <span style={{ fontSize:12, color:"var(--text-3)" }}>›</span>
        <span style={{ fontSize:12, color:"var(--text-3)", overflow:"hidden", whiteSpace:"nowrap", textOverflow:"ellipsis", maxWidth:300 }}>
          {notice.title}
        </span>
      </div>

      {/* ── 세부탭 (카테고리 > 세부탭) ──────────────────────────────── */}
      {tabItems.length > 0 && (
        <div style={{ display:"flex", gap:2, flexWrap:"wrap", borderBottom:"1px solid var(--border)", marginBottom:16 }}>
          {tabItems.map(t => {
            const on = notice.category === t.code;
            const label = t.code === topCat?.code ? `📂 ${topCat?.label}` : t.label;
            return (
              <button key={t.code} onClick={() => navigate(`/notice?filter=${t.code}`)} style={{
                padding:"7px 14px", fontSize:13, fontWeight: on ? 700 : 500,
                color: on ? "var(--p-600,#4f46e5)" : "var(--text-3)",
                background:"none", border:"none", cursor:"pointer",
                borderBottom: on ? "2px solid var(--p-500,#6366f1)" : "2px solid transparent",
                marginBottom:-1,
              }}>{label}</button>
            );
          })}
        </div>
      )}

      {/* ── Article ───────────────────────────────────────────────── */}
      <div style={{
        background:"var(--surface)", border:"1px solid var(--border)",
        borderRadius:14, overflow:"hidden",
        boxShadow:"0 2px 8px rgba(0,0,0,0.06)",
      }}>
        {/* Accent bar */}
        <div style={{ height:4, background:accent }} />

        {/* Header */}
        <div style={{ padding:"24px 32px 20px", borderBottom:"1px solid var(--border)" }}>
          <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:12 }}>
            {notice.is_pinned && <Pill kind="danger">📌 고정</Pill>}
            {cat && <Pill kind={pillKind(cat.color)}>{cat.label}</Pill>}
          </div>
          <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:16 }}>
            <h1 style={{ margin:0, fontSize:22, fontWeight:700, lineHeight:1.4, color:"var(--text)", flex:1 }}>
              {notice.title}
            </h1>
            <div style={{ display:"flex", gap:6, flexShrink:0, marginTop:2 }}>
              {canEdit && (
                <button
                  className="t-btn"
                  style={{ fontSize:13, padding:"6px 16px" }}
                  onClick={() => navigate(`/notice/${notice.id}/edit`)}
                >
                  수정
                </button>
              )}
              {canDel && (
                <button
                  className="t-btn"
                  style={{ fontSize:13, padding:"6px 16px", color:"var(--danger)" }}
                  onClick={handleDelete}
                >
                  삭제
                </button>
              )}
            </div>
          </div>
          <div style={{ display:"flex", gap:16, marginTop:12, fontSize:13, color:"var(--text-3)" }}>
            <span>작성자 · {notice.author_name ?? `#${notice.author_id}`}</span>
            <span>{new Date(notice.created_at).toLocaleString("ko-KR")}</span>
            <span>조회 {notice.view_count}</span>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding:"28px 32px 32px" }}>
          <div style={{ fontSize:15, lineHeight:2, color:"var(--text)", whiteSpace:"pre-wrap", minHeight:80 }}>
            {/* 본문 속 주소는 누르면 새 탭에서 열린다 */}
            <LinkifiedText text={notice.body}/>
          </div>
        </div>

        {/* Attachments */}
        {notice.attachments && notice.attachments.length > 0 && (
          <div style={{ padding:"0 32px 28px" }}>
            <div style={{ borderTop:"1px solid var(--border)", paddingTop:18 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"var(--text-3)", marginBottom:10 }}>
                📎 첨부파일 {notice.attachments.length}
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
                {notice.attachments.map(a => (
                  <button key={a.id} onClick={() => downloadAtt(a)} style={{
                    display:"flex", alignItems:"center", gap:10, padding:"8px 12px", borderRadius:8,
                    border:"1px solid var(--border)", background:"var(--bg-0)", cursor:"pointer",
                    textAlign:"left", width:"100%",
                  }}>
                    <span style={{ fontSize:18 }}>{fileIcon(a.content_type, a.filename)}</span>
                    <span style={{ flex:1, fontSize:13, color:"var(--text)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{a.filename}</span>
                    <span style={{ fontSize:11, color:"var(--text-3)" }}>{fmtBytes(a.size)}</span>
                    <span style={{ fontSize:13, color:"var(--p-500)" }}>⬇</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── Comments ──────────────────────────────────────────────── */}
      <div style={{
        background:"var(--surface)", border:"1px solid var(--border)",
        borderRadius:14, marginTop:16, padding:"24px 32px",
        boxShadow:"0 2px 8px rgba(0,0,0,0.06)",
      }}>
        <h3 style={{ margin:"0 0 20px", fontSize:15, fontWeight:600, color:"var(--text)" }}>
          댓글 {comments ? `${comments.length}개` : ""}
        </h3>

        {comments === null && (
          <div style={{ color:"var(--text-3)", fontSize:13, padding:"8px 0" }}>불러오는 중…</div>
        )}
        {comments?.length === 0 && (
          <div style={{ color:"var(--text-3)", fontSize:14, textAlign:"center", padding:"24px 0" }}>
            첫 댓글을 남겨보세요 ✍️
          </div>
        )}

        {comments?.map(c => (
          <div key={c.id} style={{ display:"flex", gap:12, marginBottom:14, alignItems:"flex-start" }}>
            <div style={{
              width:36, height:36, borderRadius:"50%", background:avatarBg(c.author_id), flexShrink:0,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:12, fontWeight:700, color:"#fff", letterSpacing:"-0.5px",
            }}>
              {initials(c.author_name, c.author_id)}
            </div>
            <div style={{
              flex:1, background:"rgba(0,0,0,0.03)", borderRadius:10, padding:"10px 16px",
              border:"1px solid var(--border)",
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                <span style={{ fontSize:13, fontWeight:600, color:"var(--text)" }}>
                  {c.author_name ?? `사용자 #${c.author_id}`}
                </span>
                <span style={{ fontSize:11, color:"var(--text-3)" }}>
                  {new Date(c.created_at).toLocaleString("ko-KR")}
                </span>
                {(c.author_id === currentUser?.id || isAdmin) && (
                  <button
                    onClick={() => delComment(c.id)}
                    style={{ marginLeft:"auto", background:"none", border:"none", cursor:"pointer", fontSize:11, color:"var(--danger)", padding:0 }}
                  >
                    삭제
                  </button>
                )}
              </div>
              <div style={{ fontSize:14, lineHeight:1.65, color:"var(--text)" }}><LinkifiedText text={c.body}/></div>
            </div>
          </div>
        ))}

        {/* Comment input */}
        <div style={{ display:"flex", gap:8, marginTop:20 }}>
          <input
            className="t-input"
            placeholder="댓글을 입력하세요…"
            value={newCmt}
            onChange={e => setNewCmt(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); postComment(); } }}
            disabled={posting}
            style={{ flex:1, fontSize:13 }}
          />
          <button
            className="t-btn primary"
            onClick={postComment}
            disabled={posting || !newCmt.trim()}
            style={{ flexShrink:0, fontSize:13, padding:"0 20px" }}
          >
            등록
          </button>
        </div>
      </div>
    </div>
  );
}
